import { usePrefersReducedMotion } from "@rally/ui-kit";
import { useCallback, useEffect, useRef } from "react";
import { useInACall } from "../call/calling.ts";
import { useLive } from "../chat/run/live.ts";
import { read } from "../net/rally.ts";
import { useWhere } from "../router.ts";
import { wasThatMine } from "./mine.ts";
import { began, pairIsDue, type Ringing, rang, whyItStopped } from "./ringing.ts";
import { hears, hush, lookingAt, settingsNow, soundsFor, wakeTheDevice } from "./sounding.ts";
import { type Row, whatArrived } from "./watching.ts";

const A_BURST_SETTLES_IN = 250;

const A_RING_TICK = 250;

const A_GESTURE = ["pointerdown", "keydown", "touchstart"] as const;

function useTheFirstGesture(): void {
  useEffect(() => {
    const woke = () => wakeTheDevice();
    for (const press of A_GESTURE) window.addEventListener(press, woke, { passive: true });
    return () => {
      for (const press of A_GESTURE) window.removeEventListener(press, woke);
    };
  }, []);
}

function useWhatArrived(handle: string): void {
  const before = useRef<Row[] | null>(null);
  const settling = useRef<ReturnType<typeof setTimeout> | null>(null);

  const again = useCallback(() => {
    void read<Row[]>("activity")
      .then((rows) => {
        const was = before.current;
        before.current = rows;
        if (was === null) return;
        for (const happening of whatArrived(was, rows, { handle })) hears(happening);
      })
      .catch(() => undefined);
  }, [handle]);

  useEffect(() => {
    again();
    return () => {
      if (settling.current !== null) clearTimeout(settling.current);
      settling.current = null;
    };
  }, [again]);

  useLive((notice) => {
    if (!notice.conversation) return;
    if (settling.current !== null) clearTimeout(settling.current);
    settling.current = setTimeout(again, A_BURST_SETTLES_IN);
  });
}

function inTheCall(held: { conversation: string } | null, conversation: string): boolean {
  return held?.conversation === conversation;
}

function useCallCues(): void {
  const inACall = useInACall();
  const ringing = useRef<Ringing | null>(null);
  const tick = useRef<ReturnType<typeof setInterval> | null>(null);
  const here = useRef(inACall);
  here.current = inACall;

  const stopRinging = () => {
    ringing.current = null;
    if (tick.current !== null) clearInterval(tick.current);
    tick.current = null;
    hush();
  };

  const ringOnce = () => {
    const ring = ringing.current;
    if (!ring) return;
    const now = Date.now();
    const stopped = whyItStopped(ring, now, {
      answered: inTheCall(here.current, ring.about),

      declined: !settingsNow().calls,

      left: here.current !== null && here.current.conversation !== ring.about,
    });
    if (stopped) return stopRinging();
    if (!pairIsDue(ring, now)) return;
    ringing.current = rang(ring);
    hears({ what: "ringing", conversation: ring.about, call: ring.about, by: "human" });
  };

  useLive((notice) => {
    const conversation = notice.conversation;
    if (!conversation) return;
    if (notice.kind === "call.started") {
      if (wasThatMine("started", conversation)) return;
      ringing.current = began(conversation, Date.now());

      if (tick.current === null) tick.current = setInterval(ringOnce, A_RING_TICK);
      ringOnce();
      return;
    }

    if (notice.kind === "call.joined") {
      const mine = wasThatMine("joined", conversation);

      if (mine && ringing.current?.about === conversation) stopRinging();
      if (mine || inTheCall(here.current, conversation)) {
        hears({ what: "joined a call", conversation, call: conversation, by: "human", mine });
      }
      return;
    }
    if (notice.kind === "call.left") {
      const mine = wasThatMine("left", conversation);
      if (!mine && inTheCall(here.current, conversation)) {
        hears({ what: "left a call", conversation, call: conversation, by: "human", mine });
      }
      return;
    }
    if (notice.kind === "call.ended") {
      const mine = wasThatMine("ended", conversation);
      if (ringing.current?.about === conversation) stopRinging();
      if (!mine && inTheCall(here.current, conversation)) {
        hears({ what: "call ended", conversation, call: conversation });
      }
    }
  });

  useEffect(() => {
    return () => {
      if (tick.current !== null) clearInterval(tick.current);
      tick.current = null;
    };
  }, []);
}

export function Ears({ me }: { me: { account: string; handle: string } }): null {
  const reduced = usePrefersReducedMotion();
  const where = useWhere();

  useEffect(() => {
    soundsFor(me.account, reduced);
    return () => soundsFor(null, reduced);
  }, [me.account, reduced]);

  useEffect(() => lookingAt(where.conversation ?? null), [where.conversation]);

  useTheFirstGesture();
  useWhatArrived(me.handle);
  useCallCues();
  return null;
}
